/**
 * Uploaded file → Claude content block(s), done in the browser (same rules as the original fileToPart):
 * PDF → document (a large PDF → one image per page + its text, see pdfPages), image → image, Excel/CSV → addressed rows (≤ 60,000 chars per workbook), Outlook .msg → body text
 * (≤ 40,000 chars) + its attachments, recursively. Anything else is noted and skipped.
 */
import type { Part } from "../../shared/api.ts";
import { workbookText } from "@ic/engine";

// SheetJS and the .msg reader are large and only needed for those uploads — loaded on first use
const loadXlsx = () => import("xlsx");
// msgreader is CommonJS: depending on the bundler the class is .default, .default.default or .MsgReader (the original did the same dance)
type MsgCtor = new (b: ArrayBuffer) => { getFileData(): unknown; getAttachment(a: never): unknown };
const loadMsg = async (): Promise<MsgCtor> => {
  const m = (await import("@kenjiuno/msgreader")) as unknown as Record<string, unknown> & { default?: Record<string, unknown> };
  return (typeof m.default === "function" ? m.default : m.default?.default || m.MsgReader || m.default) as unknown as MsgCtor;
};

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

/**
 * Large PDFs (JF 2026-10-12: a 14-page tech pack of 95.6 MB failed with HTTP 413 — the server takes 60 MB per request and
 * the AI about 32 MB). Above PDF_DIRECT_MAX the browser renders each page to a JPEG the size the AI reads anyway
 * (long edge 1568 px) and adds the page's text layer — that tech pack becomes ~2 MB. e2e tests lower the threshold.
 */
const PDF_DIRECT_MAX = (globalThis as { IC_PDF_DIRECT_MAX?: number }).IC_PDF_DIRECT_MAX ?? 10 * 1024 * 1024;
const PDF_MAX_PAGES = 100; // the AI's limit for one request
const PAGE_EDGE = 1568, JPEG_Q = 0.82;
// pdf.js is large and only needed for big PDFs — loaded on first use, with its worker
async function loadPdfjs() {
  const [pdfjs, worker] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

/** A large PDF → per page: the page as a JPEG, then its text (letter-spaced headings collapsed). */
export async function pdfPages(file: File, onPage?: (i: number, n: number) => void, keep?: (page: number) => boolean): Promise<Part[]> {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const doc = await task.promise;
  const n = Math.min(doc.numPages, PDF_MAX_PAGES);
  const out: Part[] = [];
  for (let i = 1; i <= n; i++) {
    if (keep && !keep(i)) continue;
    onPage?.(i, n);
    const page = await doc.getPage(i);
    const v1 = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: PAGE_EDGE / Math.max(v1.width, v1.height) });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    const blob = await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("page render failed"))), "image/jpeg", JPEG_Q));
    out.push({ type: "image", source: { type: "base64", media_type: "image/jpeg", data: await b64(blob) } });
    const tc = await page.getTextContent();
    const text = tc.items.map((x) => ("str" in x ? x.str : "")).join(" ").replace(/\s+/g, " ").trim();
    out.push({ type: "text", text: `[page ${i} of ${doc.numPages} — text]` + (text ? " " + text.slice(0, 8000) : " (no text layer)") });
    page.cleanup();
  }
  if (doc.numPages > n) out.push({ type: "text", text: `[only the first ${n} of ${doc.numPages} pages were sent]` });
  await task.destroy();
  return out;
}

function b64(file: Blob): Promise<string> {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
}

/**
 * `n` = the file's number in the upload list. PDFs and images get a "[FILE n: name]" label in front so the AI can say
 * which file / page holds the flat sketch (a few tokens); Excel / e-mail text already carries its file name.
 */
export async function fileToParts(file: File, n?: number, onPage?: (i: number, n: number) => void, pages?: PageSel): Promise<Part[]> {
  const parts = await convert(file, onPage, pages);
  if (n == null) return parts;
  return [{ type: "text", text: `[FILE ${n}: ${file.name}]` }, ...parts];
}

/**
 * One style of a file that holds several (JF 2026-10-13, e.g. a 7-style design brief): its own pages, plus the pages no
 * style claims (cover, brand rules, the request) — the other styles' pages are left out.
 */
export interface PageSel { own: number[]; others: number[] }
const keepPage = (sel: PageSel) => (p: number) => sel.own.includes(p) || !sel.others.includes(p);

async function convert(file: File, onPage?: (i: number, n: number) => void, pages?: PageSel): Promise<Part[]> {
  const name = file.name || "", low = name.toLowerCase();
  if (/\.(xlsx|xls|csv)$/.test(low)) {
    try {
      const XLSX = await loadXlsx();
      const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: "array" });
      return [{ type: "text", text: "[FILE: " + name + "]\n" + workbookText(wb) }];
    } catch { return [{ type: "text", text: "[FILE: " + name + "] (excel parse failed)" }]; }
  }
  if (/\.msg$/.test(low)) {
    try {
      const MsgReader = await loadMsg();
      const rd = new MsgReader(await file.arrayBuffer());
      const d = rd.getFileData() as { subject?: string; senderName?: string; senderEmail?: string; body?: string; attachments?: unknown[] };
      const out: Part[] = [{ type: "text", text: ("[EMAIL " + name + "]\nSubject: " + (d.subject || "") + "\nFrom: " + (d.senderName || d.senderEmail || "") + "\n\n" + (d.body || "")).slice(0, 40000) }];
      for (const [i, a] of (d.attachments || []).entries()) {
        try {
          const at = rd.getAttachment(a as never) as { content: Uint8Array; fileName?: string };
          const nf = new File([new Blob([at.content as BlobPart])], at.fileName || "attachment_" + (i + 1));
          out.push(...(await fileToParts(nf)));
        } catch { /* skip unreadable attachment */ }
      }
      return out;
    } catch { return [{ type: "text", text: "[EMAIL " + name + "] (.msg parse failed)" }]; }
  }
  const isPdf = /pdf$/i.test(file.type) || /\.pdf$/i.test(name);
  if (isPdf && pages?.own.length) return pdfPages(file, onPage, keepPage(pages));
  if (isPdf && file.size > PDF_DIRECT_MAX) return pdfPages(file, onPage);
  if (isPdf) return [{ type: "document", source: { type: "base64", media_type: "application/pdf", data: await b64(file) } }];
  const imgType = file.type || ({ png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp" } as Record<string, string>)[low.split(".").pop() || ""] || "";
  if (IMAGE_TYPES.includes(imgType)) return [{ type: "image", source: { type: "base64", media_type: imgType, data: await b64(file) } }];
  // the API accepts png / jpeg / gif / webp only — other formats (bmp, tiff, heic…) are noted instead of failing the whole request
  return [{ type: "text", text: "[FILE: " + name + "] (unsupported type, skipped)" }];
}
